import { useCallback, useState } from 'react'
import { addMonths } from 'date-fns/addMonths'
import { isAfter } from 'date-fns/isAfter'
import { parse } from 'date-fns/parse'
import {
  CheckboxWithLabel,
  Button,
  DatePicker,
  Select,
  TravelerSelection,
  cn
} from '@pcln/horizon'
import type { ITravelerInfo } from '@pcln/horizon'
import type { DateRange } from 'react-day-picker'
import { format } from 'date-fns/format'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import type { GA4PageNameType } from '@/ga4types'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import useBootstrapData from '@/hooks/useBootstrapData'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import { getGridContainerClass, getInputClass } from './HorizonCarForm.variants'
import { LABELS } from './constants'
import type { CAR_FORM_STATE_TYPE } from '../types'
import useCarFormInitialValues from './useCarFormInitialValues'
import useCarFormSubmitValues from './useCarFormSubmitValues'
import searchFormSchema from '../SearchForm/validation'
import { handleSubmitForCars } from '../SearchForm/utils'

type FormType = {
  onSubmit?: (data: CAR_FORM_STATE_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  pageName?: 'homepage' | 'landing'
}

export default function HorizonCarForm({
  onSubmit = handleSubmitForCars,
  setLocation = () => null,
  pageName = 'homepage'
}: FormType) {
  const { locale: { localeCode = 'en-US' } = {} } = useBootstrapData()
  const { initialValues, isMobile, cguid } = useCarFormInitialValues()

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
    reset
  } = useForm<CAR_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartTime,
    watchEndTime,
    watchOneWay,
    watchStartDate,
    watchEndDate,
    resetFormToInitialValues
  } = useCarFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    reset,
    initialValues,
    setLocation
  })

  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    STAY: false,
    FLY: false
  })

  const isHotel = bundleSelections.STAY
  const isFlight = bundleSelections.FLY

  const isPackage =
    watchTripType.includes('STAY') || watchTripType.includes('FLY')

  const [defaultRangeMonth, setDefaultRangeMonth] = useState(currentDate)
  const [defaultPickupMonth, setDefaultPickupMonth] = useState(currentDate)
  const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(currentDate)

  const onCalendarChange = useCallback(
    (dates: DateRange | undefined): void => {
      if (dates?.from) {
        const startDate = format(dates.from, 'yyyyMMdd')
        setValue('hotelStartDate', startDate)
        setDefaultRangeMonth(dates?.from)
      }

      if (dates?.to) {
        const endDate = format(dates.to, 'yyyyMMdd')
        setValue('hotelEndDate', endDate)
      }
    },
    [setValue]
  )

  const onSingleDateChange = useCallback(
    (date: Date): void => {
      const formattedDate = format(date, 'yyyyMMdd')
      setValue('startDate', formattedDate, { shouldDirty: true })
      setDefaultPickupMonth(date)
      // if new default pickup month is after default dropoff month, set default dropoff month to new default pickup month
      if (isAfter(date, defaultDropoffMonth)) {
        setDefaultDropoffMonth(date)
      }
    },
    [setValue, defaultDropoffMonth]
  )
  const onEndDateChange = useCallback(
    (date: Date): void => {
      const formattedDate = format(date, 'yyyyMMdd')
      setValue('endDate', formattedDate, { shouldDirty: true })
      setDefaultDropoffMonth(date)
    },
    [setValue]
  )

  const onItemSelect = useCallback(
    (
      item: LOCATION_SEARCH_TYPE | null,
      searchKey: 'startLocation' | 'endLocation'
    ) => {
      setValue(searchKey, item)
      if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue]
  )

  const handleSubmitHelper = handleSubmit((data: CAR_FORM_STATE_TYPE) => {
    onSubmit(data, pageName)
    logSearchFormAnalytics(cguid, pageName, 'cars', isMobile)
  })

  const handleTravelerChange = (travelerInfo: ITravelerInfo): void => {
    setValue('travelers', travelerInfo as TravelerState)
  }

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections(prev => ({
        ...prev,
        [optionValue]: checked
      }))
      resetFormToInitialValues({
        tripType: updatedTripType,
        startLocation: watchStartLocation,
        endLocation: watchEndLocation
      })
    },
    [resetFormToInitialValues, watchStartLocation, watchEndLocation]
  )

  const handleDifferentDropOffChange = useCallback(() => {
    const newValue = !watchOneWay
    setValue('oneWay', newValue, { shouldDirty: true })
    resetFormToInitialValues({
      oneWay: newValue,
      startLocation: watchStartLocation,
      endLocation: watchEndLocation,
      startDate: watchStartDate,
      endDate: watchEndDate,
      startTime: watchStartTime,
      endTime: watchEndTime
    })
  }, [
    setValue,
    resetFormToInitialValues,
    watchOneWay,
    watchEndDate,
    watchEndTime,
    watchStartDate,
    watchStartTime,
    watchStartLocation,
    watchEndLocation
  ])

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-car-search-form"
        onSubmit={handleSubmitHelper}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(watchOneWay, isHotel, isFlight)}
          >
            <div className={getInputClass('options')}>
              {/* Dropoff type checkbox */}
              <CheckboxWithLabel
                size="16"
                name="differentDropOff"
                id="differentDropOff"
                value="differentDropOff"
                checked={watchOneWay}
                handleChange={handleDifferentDropOffChange}
                disabled={isPackage}
                labelNode={<span>{LABELS.DIFFERENT_DROPOFF}</span>}
                labelClassName="ml-2"
              />
            </div>

            {!isFlight ? (
              <>
                {/* Pickup Location Input, For STAY package. */}
                <div className={getInputClass('pickup', watchOneWay)}>
                  <AutocompleteWrapper
                    key={`pickup-${watchTripType}`}
                    errorMessage={
                      isHotel
                        ? errors.endLocation?.message
                        : errors.startLocation?.message
                    }
                    searchKey={isHotel ? 'endLocation' : 'startLocation'}
                    label={
                      watchOneWay
                        ? LABELS.PICKUP_LOCATION
                        : LABELS.DEFAULT_PICKUP_LOCATION
                    }
                    typeAheadEndpoint="cars"
                    initialSelectedItem={
                      watchStartLocation as LOCATION_SEARCH_TYPE | undefined
                    }
                    onItemSelect={itemSelected => {
                      onItemSelect(itemSelected, 'startLocation')
                      if (isHotel) {
                        onItemSelect(itemSelected, 'endLocation')
                      }
                    }}
                  />
                </div>
                {/* Dropoff Location Input */}
                {watchOneWay && !isPackage && (
                  <div className={getInputClass('dropoff')}>
                    <AutocompleteWrapper
                      key={`dropoff-${watchTripType}`}
                      errorMessage={errors.endLocation?.message}
                      searchKey="endLocation"
                      label={LABELS.DROPOFF_LOCATION}
                      typeAheadEndpoint="cars"
                      initialSelectedItem={
                        watchEndLocation as LOCATION_SEARCH_TYPE | undefined
                      }
                      onItemSelect={itemSelected => {
                        onItemSelect(itemSelected, 'endLocation')
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="col-span-full sm:[grid-area:unset]">
                  <AutocompleteWrapper
                    key={`flight-origin-${watchTripType}`}
                    errorMessage={errors.startLocation?.message}
                    searchKey="startLocation"
                    label="Origin"
                    typeAheadEndpoint="flights"
                    initialSelectedItem={
                      watchStartLocation as LOCATION_SEARCH_TYPE | undefined
                    }
                    onItemSelect={itemSelected => {
                      onItemSelect(itemSelected, 'startLocation')
                    }}
                  />
                </div>
                <div className="col-span-full sm:[grid-area:unset]">
                  <AutocompleteWrapper
                    key={`flight-destination-${watchTripType}`}
                    errorMessage={errors.endLocation?.message}
                    searchKey="endLocation"
                    label="Destination"
                    typeAheadEndpoint="flights"
                    initialSelectedItem={
                      watchEndLocation as LOCATION_SEARCH_TYPE | undefined
                    }
                    onItemSelect={itemSelected => {
                      onItemSelect(itemSelected, 'endLocation')
                    }}
                  />
                </div>
              </>
            )}

            {!isPackage ? (
              <>
                {/* Pickup date */}
                <div className="**:translate-y-0">
                  <DatePicker
                    key={`pickup-date-${watchTripType}`}
                    errorMessage={errors.startDate?.message}
                    isMobile={isMobile}
                    label={LABELS.PICKUP_DATE}
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={onSingleDateChange}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultPickupMonth}
                  />
                </div>
                {/* Pickup time */}
                <Select
                  label={LABELS.PICKUP_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  menuWidth="150px"
                  initialValue={watchStartTime}
                  onChange={(value: string) => setValue('startTime', value)}
                  placeholder={LABELS.PICKUP_TIME}
                />
                {/* Dropoff date */}
                <div className="**:translate-y-0">
                  <DatePicker
                    key={`dropoff-date-${watchTripType}`}
                    errorMessage={errors.endDate?.message}
                    isMobile={isMobile}
                    label={LABELS.DROPOFF_DATE}
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={onEndDateChange}
                    disabled={{
                      before: watchStartDate
                        ? parse(watchStartDate, 'yyyyMMdd', currentDate)
                        : currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultDropoffMonth}
                  />
                </div>
                {/* Dropoff time */}
                <Select
                  label={LABELS.DROPOFF_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  menuWidth="150px"
                  initialValue={watchEndTime}
                  onChange={(value: string) => setValue('endTime', value)}
                  placeholder={LABELS.DROPOFF_TIME}
                />
              </>
            ) : (
              <>
                <div className="**:translate-y-0">
                  <DatePicker
                    key={`package-dates-${watchTripType}`}
                    errorMessage={
                      errors.hotelEndDate?.message ||
                      errors.hotelStartDate?.message
                    }
                    isMobile={isMobile}
                    className="flight-dates"
                    title="Departing – Returning"
                    label="Departing – Returning"
                    mode="range"
                    numberOfMonths={isMobile ? 12 : 2}
                    handleCalendarChange={onCalendarChange}
                    defaultMonth={defaultRangeMonth}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    allowSameDayInRange={!isHotel}
                  />
                </div>
                <div className="**:translate-y-0">
                  <TravelerSelection
                    isMobile={isMobile}
                    isHotel={isHotel && !isFlight}
                    initialValues={initialValues.travelers}
                    roomsEnabled={isHotel}
                    maxRooms={4}
                    maxChildren={6}
                    maxTravelers={8}
                    ageSelectEnabled
                    tooltipErrorMessageId="travelerSelectionError"
                    errorMessage={
                      errors?.travelers?.childrenAges?.message ||
                      errors?.travelers?.children?.message ||
                      errors?.travelers?.rooms?.message ||
                      ''
                    }
                    onChange={(values: ITravelerInfo) => {
                      handleTravelerChange(values)
                    }}
                    collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                    disclaimer="You may only book a package for up to 8 travelers."
                  />
                </div>
              </>
            )}

            {/* Bundle and Save */}
            <div className={cn(getInputClass('package'))}>
              <HorizonBundleAndSave
                productType="cars"
                checkedValues={bundleSelections}
                onSelectionChange={handleBundleSelectionChange}
                tripType={watchTripType}
              />
            </div>

            {/* Search Button */}
            <Button
              type="primary"
              size="lg"
              buttonType="submit"
              className={cn(
                getInputClass('submit'),
                isPackage && 'sm:col-span-1',
                isMobile ? 'fixed bottom-4 right-4 left-4' : ''
              )}
            >
              {LABELS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
